'use client'

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { useAppSelector, useAppDispatch } from '@/store'
import { clearUser as clearUserAction, setCurrentUser } from '@/store/currentUserSlice'
import { setHasSession } from '@/store/sessionSlice'
import { User } from '@/types'
import { useRouter } from '@/i18n/routing'

// Public pages where auth is not required (without locale prefix)
const PUBLIC_PAGES = ['/sign-in', '/invite', '/forgot-password', '/reset-password']

function isPublicPage(pathname: string): boolean {
  // Strip locale prefix (e.g. /de/invite -> /invite)
  const withoutLocale = pathname.replace(/^\/(en|de)/, '') || '/'
  return PUBLIC_PAGES.some(p => withoutLocale === p || withoutLocale.startsWith(p + '/'))
}

// Types for the auth context
interface AuthContextType {
  currentUser: User | null
  loading: boolean
  error: string | null
  fetchCurrentUser: (force?: boolean) => Promise<User | null>
  clearUser: () => void
  setUser: (user: User) => void
  isAuthenticated: boolean
}

// Debounce delay for fetch operations (in ms)
const FETCH_DEBOUNCE_DELAY = 1000

// Minimum time between fetches (in ms)
const MIN_FETCH_INTERVAL = 5000

// Auto-refresh interval (in ms)
const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// Auth Provider Component
export function AuthContextProvider({ children }: AuthProviderProps) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const pathname = usePathname()
  const currentUser = useAppSelector((state) => state.user.currentUser)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Refs for mutable state that shouldn't trigger re-renders
  const isRedirectingRef = useRef(false)
  const isFetchingRef = useRef(false)
  const lastFetchTimeRef = useRef(0)
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const fetchTimeoutIdRef = useRef<NodeJS.Timeout | null>(null)

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    if (isRedirectingRef.current) {
      return
    }

    // Don't redirect to sign-in if already on a public page
    if (isPublicPage(pathname)) {
      return
    }

    isRedirectingRef.current = true
    dispatch(clearUserAction())

    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }

    setTimeout(() => {
      router.push('/sign-in')
      setTimeout(() => {
        isRedirectingRef.current = false
      }, 1000)
    }, 100)
  }, [dispatch, router, pathname])

  // Core fetch function
  const fetchUser = useCallback(async (force = false): Promise<User | null> => {
    if (isRedirectingRef.current) {
      return null
    }

    if (isFetchingRef.current && !force) {
      return null
    }

    const now = Date.now()
    if (!force && now - lastFetchTimeRef.current < MIN_FETCH_INTERVAL) {
      return null
    }

    isFetchingRef.current = true
    lastFetchTimeRef.current = now

    try {
      const response = await fetch('/api/auth/current-user')

      if (response.ok) {
        const data = await response.json()
        if (data.data) {
          dispatch(setCurrentUser(data.data))
          dispatch(setHasSession(true))
          isRedirectingRef.current = false
          return data.data as User
        }
      } else if (response.status === 401) {
        handleSessionExpired()
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err)
    } finally {
      isFetchingRef.current = false
    }

    return null
  }, [dispatch, handleSessionExpired])

  // Debounced fetch function
  const debouncedFetch = useCallback((force = false): Promise<User | null> => {
    if (fetchTimeoutIdRef.current) {
      clearTimeout(fetchTimeoutIdRef.current)
    }

    if (force) {
      return fetchUser(true)
    }

    return new Promise((resolve) => {
      fetchTimeoutIdRef.current = setTimeout(async () => {
        const user = await fetchUser(false)
        resolve(user)
      }, FETCH_DEBOUNCE_DELAY)
    })
  }, [fetchUser])

  // Public fetch function with loading state
  const fetchCurrentUser = useCallback(async (force = false): Promise<User | null> => {
    setLoading(true)
    setError(null)
    try {
      return await debouncedFetch(force)
    } catch (err) {
      console.error(err)
      setError('Failed to fetch user data')
      return null
    } finally {
      setLoading(false)
    }
  }, [debouncedFetch])

  // Clear user function
  const clearUser = useCallback(() => {
    dispatch(clearUserAction())
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
    if (fetchTimeoutIdRef.current) {
      clearTimeout(fetchTimeoutIdRef.current)
      fetchTimeoutIdRef.current = null
    }
  }, [dispatch])

  // Set user function
  const setUser = useCallback((user: User) => {
    dispatch(setCurrentUser(user))
  }, [dispatch])

  // Initial fetch and auto-refresh setup
  useEffect(() => {
    // Skip auth fetch on public pages (sign-in, invite)
    if (!isRedirectingRef.current && !isPublicPage(pathname)) {
      if (!currentUser || !currentUser.token) {
        // No user yet — or a rehydrated user without its auth token (the JWT
        // is never persisted to storage) — force an immediate fetch so
        // sockets/REST get a token without waiting for the debounce window.
        void fetchCurrentUser(true)
      } else {
        // User exists — refresh session data on navigation (debounced, respects MIN_FETCH_INTERVAL)
        void debouncedFetch(false)
      }
    }

    // Clear existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }

    // Set up auto-refresh only if user exists
    if (currentUser) {
      intervalIdRef.current = setInterval(() => {
        if (!isRedirectingRef.current) {
          void debouncedFetch(false)
        }
      }, AUTO_REFRESH_INTERVAL)
    }

    // Handle window focus
    const handleFocus = () => {
      if (currentUser && !isRedirectingRef.current) {
        void debouncedFetch(false)
      }
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
      }
      if (fetchTimeoutIdRef.current) {
        clearTimeout(fetchTimeoutIdRef.current)
      }
    }
  }, [currentUser, fetchCurrentUser, debouncedFetch, pathname])

  const contextValue: AuthContextType = {
    currentUser,
    loading,
    error,
    fetchCurrentUser,
    clearUser,
    setUser,
    isAuthenticated: !!currentUser
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthContextProvider')
  }
  return context
}
